import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	Card,
	CardContent
} from "@mui/material";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import "./logDetailsDialog.css";
import { IVehicleEventDetails } from "..";
import { BBOX_STROKE_COLOR, BBOX_STROKE_WIDTH, EVENT_LOGS_REQUEST_TIME_FORMAT } from "../../../utils/constants";
import { convertToTitleCase } from "../../../utils/commonUtils";
import { ICoordinate, IDimensions } from "../../../common/CameraSetup/interfaces";
import EvidenceImage, { ICarouselOverlayDetails } from "../../../common/EvidenceImage";

interface ILogDetailsDialogProps {
	open: boolean;
	eventLogDetails: IVehicleEventDetails | null;
	previousButtonDisabled?: boolean;
	nextButtonDisabled?: boolean;
	pageChangeLoading?: boolean;
	onClose: () => void;
	onPrevious: (eventId: string) => void;
	onNext: (eventId: string) => void;
}

export interface IFullscreenImageProps {
	src: string;
	bbox: [number, number, number, number][];
	imageDimensions: IDimensions;
	overlayDetails: ICarouselOverlayDetails;
}

const FullscreenImage: FC<IFullscreenImageProps> = (props) => {
	const { src, bbox, imageDimensions, overlayDetails } = props;

	const canvasRef = useRef<HTMLCanvasElement>(null);

	const [canvasDimensions, setCanvasDimensions] = useState<IDimensions>({ height: 0, width: 0 });

	// Calculate font sizes based on canvas width
	const fontSizes = useMemo(() => {
		const baseWidth = 800; // Base width for reference
		const scaleFactor = Math.max(0.5, Math.min(1.5, canvasDimensions.width / baseWidth));

		return {
			productName: Math.max(10, Math.min(20, 16 * scaleFactor)),
			clientName: Math.max(8, Math.min(16, 14 * scaleFactor)),
			timestamp: Math.max(8, Math.min(14, 12 * scaleFactor)),
			content: Math.max(8, Math.min(14, 12 * scaleFactor))
		};
	}, [canvasDimensions.width]);

	const getScaledCoordinate = useCallback(
		(coordinate: ICoordinate): ICoordinate => {
			const { height: imageHeight, width: imageWidth } = imageDimensions;
			const { height: canvasHeight, width: canvasWidth } = canvasDimensions;

			return {
				x: (canvasWidth * coordinate.x) / imageWidth,
				y: (canvasHeight * coordinate.y) / imageHeight
			};
		},
		[canvasDimensions, imageDimensions]
	);

	useEffect(() => {
		if (src && canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [src, canvasRef.current]);

	useEffect(() => {
		if (canvasDimensions.width) {
			const canvasHeight = (canvasDimensions.width * imageDimensions.height) / imageDimensions.width;

			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				height: canvasHeight
			}));
		}
	}, [canvasDimensions.width, imageDimensions, src]);

	useEffect(() => {
		if (canvasRef.current && src) {
			// eslint-disable-next-line no-self-assign
			canvasRef.current.width = canvasRef.current.width;

			const canvasContext = canvasRef.current.getContext("2d");
			if (!canvasContext) return;

			canvasContext.strokeStyle = BBOX_STROKE_COLOR;
			canvasContext.lineWidth = BBOX_STROKE_WIDTH;

			for (const bboxItem of bbox) {
				const startCoordinate = getScaledCoordinate({ x: bboxItem[0], y: bboxItem[1] });
				const bboxDimensions = getScaledCoordinate({ x: bboxItem[2], y: bboxItem[3] });

				canvasContext.strokeRect(startCoordinate.x, startCoordinate.y, bboxDimensions.x, bboxDimensions.y);
			}
		}
	}, [bbox, getScaledCoordinate, src]);

	return (
		<Box className="evidence-image-wrapper">
			<Box className="evidence-image-overlay top" sx={{ padding: { xs: "8px 12px", sm: "16px 24px" } }}>
				<Box className="evidence-image-overlay-header-wrapper">
					<Box className="evidence-image-overlay-header">
						<h2 style={{ fontSize: `${fontSizes.productName}px` }}>{overlayDetails.productName}</h2>
						<Box className="evidence-image-overlay-header-divider" />
						<h3 style={{ fontSize: `${fontSizes.clientName}px` }}>{overlayDetails.clientName}</h3>
					</Box>

					<Box className="evidence-image-overlay-timestamp-wrapper">
						<h4 style={{ fontSize: `${fontSizes.timestamp}px` }}>{`Date: ${moment(overlayDetails.timestamp).format(
							"YYYY-MM-DD"
						)}`}</h4>
						<Box className="evidence-image-overlay-header-divider" />
						<h4 style={{ fontSize: `${fontSizes.timestamp}px` }}>{`Time: ${moment(overlayDetails.timestamp).format(
							"hh:mm A"
						)}`}</h4>
					</Box>
				</Box>
			</Box>

			<Box sx={{ width: "100%", height: canvasDimensions.height, position: "relative" }}>
				<canvas
					ref={canvasRef}
					width={canvasDimensions.width}
					height={canvasDimensions.height}
					style={{
						width: "100%",
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
						backgroundImage: `url(${src})`
					}}
				/>
			</Box>

			<Box className="evidence-image-overlay bottom" sx={{ padding: { xs: "8px 12px", sm: "16px 24px" } }}>
				<Box className="evidence-image-overlay-content">
					<Box className="evidence-image-overlay-content-item" sx={{ flexShrink: 0 }}>
						<h5 style={{ whiteSpace: "nowrap", fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Plate Number: </span>
							{overlayDetails.numberPlate}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Vehicle Type: </span>
							{overlayDetails.vehicleType}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Location: </span>
							{overlayDetails.location}
						</h5>
					</Box>

					<Box className="evidence-image-overlay-content-item">
						<h5 style={{ fontSize: `${fontSizes.content}px` }}>
							<span className="evidence-image-overlay-content-name">Type: </span>
							{overlayDetails.auth}
						</h5>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const LogDetailsDialog: FC<ILogDetailsDialogProps> = (props) => {
	const {
		open,
		eventLogDetails,
		previousButtonDisabled,
		nextButtonDisabled,
		pageChangeLoading,
		onClose,
		onPrevious,
		onNext
	} = props;

	const dialogContentRef = useRef<HTMLDivElement>(null);

	const [fullscreenImageProps, setFullscreenImageProps] = useState<IFullscreenImageProps | null>(null);

	const [touchStart, setTouchStart] = useState<number | null>(null);
	const [touchEnd, setTouchEnd] = useState<number | null>(null);

	// Minimum swipe distance (in px)
	const minSwipeDistance = 50;

	function handleShowFullscreenImage(props: IFullscreenImageProps) {
		setFullscreenImageProps(props);
	}

	function handleHideFullscreenImage() {
		setFullscreenImageProps(null);
	}

	// Touch handlers for swipe gestures
	const onTouchStart = (e: React.TouchEvent) => {
		setTouchEnd(null);
		setTouchStart(e.targetTouches[0].clientX);
	};

	const onTouchMove = (e: React.TouchEvent) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};

	const onTouchEnd = () => {
		if (!touchStart || !touchEnd) return;

		const distance = touchStart - touchEnd;
		const isLeftSwipe = distance > minSwipeDistance;
		const isRightSwipe = distance < -minSwipeDistance;

		if (isLeftSwipe && !nextButtonDisabled) {
			onNext(eventLogDetails?.unique_id ?? "");
		}
		if (isRightSwipe && !previousButtonDisabled) {
			onPrevious(eventLogDetails?.unique_id ?? "");
		}
	};

	const keypressEventListener = useCallback(
		(event: KeyboardEvent) => {
			if (open) {
				if (event.key === "ArrowLeft") {
					event.preventDefault();
					onPrevious(eventLogDetails?.unique_id ?? "");
				} else if (event.key === "ArrowRight") {
					event.preventDefault();
					onNext(eventLogDetails?.unique_id ?? "");
				} else if (event.key === "Escape") {
					if (fullscreenImageProps) handleHideFullscreenImage();
				}
			}
		},
		[eventLogDetails?.unique_id, fullscreenImageProps, onNext, onPrevious, open]
	);

	useEffect(() => {
		document.addEventListener("keydown", keypressEventListener, false);
		return () => {
			document.removeEventListener("keydown", keypressEventListener, false);
		};
	}, [keypressEventListener]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="xl"
			disableEscapeKeyDown={!!fullscreenImageProps}
			sx={{
				"& .MuiDialog-paper": {
					margin: { xs: "16px", sm: "24px" },
					maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 48px)" },
					width: { xs: "calc(100vw - 32px)", sm: "calc(100vw - 48px)" },
					overflow: "visible"
				}
			}}
		>
			<DialogTitle
				textAlign="center"
				sx={{
					background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
					color: "white",
					position: "relative",
					padding: { xs: "12px 16px 8px 16px", sm: "16px 20px 12px 20px" },
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
					overflow: "visible"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<VisibilityIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
					<Typography
						sx={{
							fontWeight: 600,
							fontSize: { xs: "1.125rem", sm: "1.25rem" }
						}}
					>
						Log Details
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: { xs: 8, sm: 12 }, right: { xs: 8, sm: 12 } }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: { xs: "4px", sm: "6px" },
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent
				ref={dialogContentRef}
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				sx={{
					padding: { xs: "16px !important", sm: "20px !important" },
					background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)"
				}}
			>
				{eventLogDetails ? (
					<>
						<Box
							className={`log-change-arrow left ${previousButtonDisabled ? "disabled" : ""}`}
							onClick={() => onPrevious(eventLogDetails.unique_id)}
						>
							<ChevronLeftIcon
								color={previousButtonDisabled ? "disabled" : "inherit"}
								sx={{ fontSize: { xs: "32px", sm: "48px" } }}
							/>
						</Box>

						<Box
							className={`log-change-arrow right ${nextButtonDisabled ? "disabled" : ""}`}
							onClick={() => onNext(eventLogDetails.unique_id)}
						>
							<ChevronRightIcon
								color={nextButtonDisabled ? "disabled" : "inherit"}
								sx={{ fontSize: { xs: "32px", sm: "48px" } }}
							/>
						</Box>

						<Grid container spacing={2} columns={10}>
							<Grid item xs={12} md={7}>
								<Box className="log-details-dialog-header">
									<EvidenceImage
										vehicleLogDetails={eventLogDetails}
										bbox={eventLogDetails.bbox}
										onOpenFullscreenImage={handleShowFullscreenImage}
									/>
								</Box>
							</Grid>

							<Grid item xs={12} md={3}>
								<Grid container spacing={{ xs: 1, sm: 2 }}>
									{/* Plate Image Card: 3 columns on md+ */}
									{eventLogDetails.plate_image ? (
										<Grid item xs={12}>
											<Card
												sx={{
													background: "linear-gradient(135deg, #fff 0%, #f8f9ff 100%)",
													border: "1px solid rgba(91, 78, 191, 0.08)",
													borderRadius: "8px",
													boxShadow: "0px 1px 4px rgba(91, 78, 191, 0.04)",
													padding: 0,
													marginBottom: 0,
													display: "flex",
													flexDirection: "column",
													// alignItems: "center",
													// justifyContent: "center",
													minHeight: { xs: 120, sm: 180 },
													height: "100%"
												}}
											>
												<CardContent
													sx={{
														width: "100%",
														display: "flex",
														flexDirection: "column",
														// alignItems: "center",
														// justifyContent: "center",
														p: { xs: 1.5, sm: 2 },
														flex: 1
													}}
												>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														<Box
															sx={{
																width: "3px",
																height: "18px",
																background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
																borderRadius: "2px"
															}}
														/>
														<Typography
															variant="subtitle1"
															sx={{
																fontWeight: 600,
																color: "#2c3e50",
																fontSize: { xs: "0.875rem", sm: "1rem" },
																letterSpacing: 0.5
															}}
														>
															Plate Image
														</Typography>
													</Box>

													<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
														<Box
															component="img"
															src={eventLogDetails.plate_image}
															alt="Plate"
															sx={{
																width: "100%",
																// maxWidth: 180,
																maxHeight: { xs: 40, sm: 60 },
																borderRadius: "6px",
																border: "1px solid #e0e0e0",
																objectFit: "contain",
																background: "#fff",
																boxShadow: "0 1px 4px rgba(91,78,191,0.06)",
																flex: 1
															}}
														/>
													</Box>
												</CardContent>
											</Card>
										</Grid>
									) : null}

									{/* Event Details Card: 9 columns on md+ */}
									<Grid item xs={12}>
										<Card
											sx={{
												background: "linear-gradient(135deg, #fff 0%, #f8f9ff 100%)",
												border: "1px solid rgba(91, 78, 191, 0.08)",
												borderRadius: "8px",
												boxShadow: "0px 1px 4px rgba(91, 78, 191, 0.04)",
												padding: 0,
												marginBottom: 0
											}}
										>
											<CardContent
												sx={{ padding: { xs: "12px 12px 6px 12px !important", sm: "16px 16px 8px 16px !important" } }}
											>
												<Grid container alignItems="center" spacing={1} sx={{ marginBottom: 1 }}>
													<Grid item xs={12}>
														<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
															<Box
																sx={{
																	width: "3px",
																	height: "18px",
																	background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
																	borderRadius: "2px"
																}}
															/>
															<Typography
																variant="subtitle1"
																sx={{
																	fontWeight: 600,
																	color: "#2c3e50",
																	fontSize: { xs: "0.875rem", sm: "1rem" },
																	letterSpacing: 0.5
																}}
															>
																Event Details
															</Typography>
														</Box>
													</Grid>
												</Grid>

												<Grid container spacing={{ xs: 1, sm: 1 }}>
													{/* Name */}
													<Grid item xs={12}>
														<Box
															sx={{
																background: "rgba(255,255,255,0.6)",
																borderRadius: "6px",
																padding: { xs: "6px 8px", sm: "8px 10px" },
																border: "1px solid #f0f0fa"
															}}
														>
															<Typography
																sx={{
																	color: "#5b4ebf",
																	fontWeight: 600,
																	fontSize: { xs: "0.7rem", sm: "0.75rem" },
																	textTransform: "uppercase",
																	letterSpacing: 0.3,
																	marginBottom: "2px"
																}}
															>
																Name
															</Typography>
															<Typography
																sx={{
																	fontWeight: 500,
																	color: "#2c3e50",
																	fontSize: { xs: "0.875rem", sm: "0.93rem" }
																}}
															>
																{eventLogDetails.name}
															</Typography>
														</Box>
													</Grid>

													{/* Location */}
													{eventLogDetails.location && (
														<Grid item xs={12}>
															<Box
																sx={{
																	background: "rgba(255,255,255,0.6)",
																	borderRadius: "6px",
																	padding: { xs: "6px 8px", sm: "8px 10px" },
																	border: "1px solid #f0f0fa"
																}}
															>
																<Typography
																	sx={{
																		color: "#5b4ebf",
																		fontWeight: 600,
																		fontSize: { xs: "0.7rem", sm: "0.75rem" },
																		textTransform: "uppercase",
																		letterSpacing: 0.3,
																		marginBottom: "2px"
																	}}
																>
																	Location
																</Typography>
																<Typography
																	sx={{
																		fontWeight: 500,
																		color: "#2c3e50",
																		fontSize: { xs: "0.875rem", sm: "0.93rem" }
																	}}
																>
																	{eventLogDetails.location}
																</Typography>
															</Box>
														</Grid>
													)}

													{/* Plate Number */}
													<Grid item xs={12}>
														<Box
															sx={{
																background: "rgba(255,255,255,0.6)",
																borderRadius: "6px",
																padding: { xs: "6px 8px", sm: "8px 10px" },
																border: "1px solid #f0f0fa"
															}}
														>
															<Typography
																sx={{
																	color: "#5b4ebf",
																	fontWeight: 600,
																	fontSize: { xs: "0.7rem", sm: "0.75rem" },
																	textTransform: "uppercase",
																	letterSpacing: 0.3,
																	marginBottom: "2px"
																}}
															>
																Plate Number
															</Typography>
															<Typography
																sx={{
																	fontWeight: 500,
																	color: "#2c3e50",
																	fontSize: { xs: "0.875rem", sm: "0.93rem" }
																}}
															>
																{eventLogDetails.vehicle_number}
															</Typography>
														</Box>
													</Grid>

													{/* Flat/Unit Number */}
													{eventLogDetails.flat_number && (
														<Grid item xs={12}>
															<Box
																sx={{
																	background: "rgba(255,255,255,0.6)",
																	borderRadius: "6px",
																	padding: { xs: "6px 8px", sm: "8px 10px" },
																	border: "1px solid #f0f0fa"
																}}
															>
																<Typography
																	sx={{
																		color: "#5b4ebf",
																		fontWeight: 600,
																		fontSize: { xs: "0.7rem", sm: "0.75rem" },
																		textTransform: "uppercase",
																		letterSpacing: 0.3,
																		marginBottom: "2px"
																	}}
																>
																	{eventLogDetails.type === "authorized" ? "Unit Number" : "Area of Visit"}
																</Typography>
																<Typography
																	sx={{
																		fontWeight: 500,
																		color: "#2c3e50",
																		fontSize: { xs: "0.875rem", sm: "0.93rem" }
																	}}
																>
																	{eventLogDetails.flat_number}
																</Typography>
															</Box>
														</Grid>
													)}

													{/* Timestamp */}
													<Grid item xs={12}>
														<Box
															sx={{
																background: "rgba(255,255,255,0.6)",
																borderRadius: "6px",
																padding: { xs: "6px 8px", sm: "8px 10px" },
																border: "1px solid #f0f0fa"
															}}
														>
															<Typography
																sx={{
																	color: "#5b4ebf",
																	fontWeight: 600,
																	fontSize: { xs: "0.7rem", sm: "0.75rem" },
																	textTransform: "uppercase",
																	letterSpacing: 0.3,
																	marginBottom: "2px"
																}}
															>
																Timestamp
															</Typography>
															<Typography
																sx={{
																	fontWeight: 500,
																	color: "#2c3e50",
																	fontSize: { xs: "0.875rem", sm: "0.93rem" }
																}}
															>
																{moment(eventLogDetails.timestamp, EVENT_LOGS_REQUEST_TIME_FORMAT).format(
																	"DD MMM YYYY, hh:mm A"
																)}
															</Typography>
														</Box>
													</Grid>

													{/* Type */}
													<Grid item xs={12}>
														<Box
															sx={{
																background: "rgba(255,255,255,0.6)",
																borderRadius: "6px",
																padding: { xs: "6px 8px", sm: "8px 10px" },
																border: "1px solid #f0f0fa"
															}}
														>
															<Typography
																sx={{
																	color: "#5b4ebf",
																	fontWeight: 600,
																	fontSize: { xs: "0.7rem", sm: "0.75rem" },
																	textTransform: "uppercase",
																	letterSpacing: 0.3,
																	marginBottom: "2px"
																}}
															>
																Type
															</Typography>
															<Typography
																sx={{
																	fontWeight: 500,
																	color: "#2c3e50",
																	fontSize: { xs: "0.875rem", sm: "0.93rem" }
																}}
															>
																{convertToTitleCase(eventLogDetails.type)}
															</Typography>
														</Box>
													</Grid>

													{/* Contact Number (if visitor) */}
													{eventLogDetails.type === "visitor" && eventLogDetails.contact_number && (
														<Grid item xs={12}>
															<Box
																sx={{
																	background: "rgba(255,255,255,0.6)",
																	borderRadius: "6px",
																	padding: { xs: "6px 8px", sm: "8px 10px" },
																	border: "1px solid #f0f0fa"
																}}
															>
																<Typography
																	sx={{
																		color: "#5b4ebf",
																		fontWeight: 600,
																		fontSize: { xs: "0.7rem", sm: "0.75rem" },
																		textTransform: "uppercase",
																		letterSpacing: 0.3,
																		marginBottom: "2px"
																	}}
																>
																	Contact Number
																</Typography>
																<Typography
																	sx={{
																		fontWeight: 500,
																		color: "#2c3e50",
																		fontSize: { xs: "0.875rem", sm: "0.93rem" }
																	}}
																>
																	{eventLogDetails.contact_number}
																</Typography>
															</Box>
														</Grid>
													)}

													{/* Address (if visitor) */}
													{eventLogDetails.type === "visitor" && eventLogDetails.address && (
														<Grid item xs={12}>
															<Box
																sx={{
																	background: "rgba(255,255,255,0.6)",
																	borderRadius: "6px",
																	padding: { xs: "6px 8px", sm: "8px 10px" },
																	border: "1px solid #f0f0fa"
																}}
															>
																<Typography
																	sx={{
																		color: "#5b4ebf",
																		fontWeight: 600,
																		fontSize: { xs: "0.7rem", sm: "0.75rem" },
																		textTransform: "uppercase",
																		letterSpacing: 0.3,
																		marginBottom: "2px"
																	}}
																>
																	Address
																</Typography>
																<Typography
																	sx={{
																		fontWeight: 500,
																		color: "#2c3e50",
																		fontSize: { xs: "0.875rem", sm: "0.93rem" }
																	}}
																>
																	{eventLogDetails.address}
																</Typography>
															</Box>
														</Grid>
													)}

													{/* Custom Fields */}
													{eventLogDetails.custom_field_data &&
														Object.keys(eventLogDetails.custom_field_data).length > 0 &&
														Object.entries(eventLogDetails.custom_field_data).map(([key, value]) =>
															value ? (
																<Grid item xs={12} key={key}>
																	<Box
																		sx={{
																			background: "rgba(255,255,255,0.6)",
																			borderRadius: "6px",
																			padding: { xs: "6px 8px", sm: "8px 10px" },
																			border: "1px solid #f0f0fa"
																		}}
																	>
																		<Typography
																			sx={{
																				color: "#5b4ebf",
																				fontWeight: 600,
																				fontSize: { xs: "0.7rem", sm: "0.75rem" },
																				textTransform: "uppercase",
																				letterSpacing: 0.3,
																				marginBottom: "2px"
																			}}
																		>
																			{key}
																		</Typography>
																		<Typography
																			sx={{
																				fontWeight: 500,
																				color: "#2c3e50",
																				fontSize: { xs: "0.875rem", sm: "0.93rem" }
																			}}
																		>
																			{value}
																		</Typography>
																	</Box>
																</Grid>
															) : null
														)}
												</Grid>
											</CardContent>
										</Card>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</>
				) : pageChangeLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							padding: { xs: "24px", sm: "40px" }
						}}
					>
						<CircularProgress sx={{ color: "#5b4ebf" }} />
					</Box>
				) : null}
			</DialogContent>

			{fullscreenImageProps ? (
				<Box className="fullscreen-evidence-image-section" onClick={handleHideFullscreenImage}>
					<Box className="fullscreen-evidence-image-wrapper" onClick={(event) => event.stopPropagation()}>
						<FullscreenImage {...fullscreenImageProps} />
					</Box>

					<IconButton
						disableRipple
						sx={{
							position: "absolute",
							top: "16px",
							right: "16px",
							background: "rgba(0, 0, 0, 0.5)",
							backdropFilter: "blur(8px)",
							"&:hover": {
								background: "rgba(0, 0, 0, 0.7)"
							}
						}}
						onClick={handleHideFullscreenImage}
					>
						<FullscreenExitIcon sx={{ color: "white" }} fontSize="large" />
					</IconButton>
				</Box>
			) : null}
		</Dialog>
	);
};

export default LogDetailsDialog;
